import React, { useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Grid, Divider,
  IconButton, Paper, alpha, useTheme, Stack, Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const InvoiceModal = ({ open, onClose, invoiceData, loading }) => {
  const theme = useTheme();
  const invoiceRef = useRef();

  const handleDownloadPDF = async () => {
    if (!invoiceData) return;
    const element = invoiceRef.current;
    try {
      const canvas = await html2canvas(element, {
        scale: 3, // Higher scale for production-quality text sharpness
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      // Maintain aspect ratio without overflow
      const pdfHeight = Math.min((canvas.height * pdfWidth) / canvas.width, 280); 
      
      pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight);
      pdf.save(`invoice_${invoiceData?.payment_id || 'PROFORMA'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: 'white'
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReceiptIcon />
          <Typography variant="h6" fontWeight="bold">Invoice Details</Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', py: 4 }}>
        {(!invoiceData && !loading) ? (
          <Stack alignItems="center" py={10}>
            <Typography variant="h6" color="text.secondary">Invoice not available</Typography>
          </Stack>
        ) : (
          <Paper ref={invoiceRef} elevation={0} sx={{ p: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" color="primary" fontWeight="800">GOLD PETALS</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">PREMIUM FAMILY NETWORK</Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="h4" fontWeight="900" sx={{ opacity: 0.1, mb: -1 }}>INVOICE</Typography>
                <Typography variant="subtitle2" fontWeight="bold">#{invoiceData?.id || 'N/A'}</Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={4}>
              {/* Invoice Details */}
              <Grid item xs={6}>
                <Typography variant="overline" color="text.secondary" fontWeight="bold">Invoice Details</Typography>
                <Box mt={1}>
                  <Typography variant="body2"><strong>Order ID:</strong> {invoiceData?.order_id || '—'}</Typography>
                  <Typography variant="body2"><strong>Payment ID:</strong> {invoiceData?.payment_id || 'Pending'}</Typography>
                  <Typography variant="body2"><strong>Date:</strong> {invoiceData?.created_at ? new Date(invoiceData.created_at).toLocaleDateString() : '—'}</Typography>
                </Box>
              </Grid>

              {/* User Details */}
              <Grid item xs={6}>
                <Typography variant="overline" color="text.secondary" fontWeight="bold">Billed To</Typography>
                <Box mt={1}>
                  <Typography variant="body2" fontWeight="bold">{invoiceData?.first_name} {invoiceData?.last_name}</Typography>
                  <Typography variant="body2" color="text.secondary">UID: {invoiceData?.uid || '—'}</Typography>
                  <Typography variant="body2" color="text.secondary">{invoiceData?.email_id || 'No Email Provided'}</Typography>
                </Box>
              </Grid>

              {/* Plan Table */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" fontWeight="bold">Description</Typography>
                    <Typography variant="subtitle2" fontWeight="bold">Amount</Typography>
                  </Box>
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Gold Petals Premium Plan</Typography>
                      <Typography variant="caption" color="text.secondary">Lifetime Access Membership</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">₹{invoiceData?.amount || '0'}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">Total Paid</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">₹{invoiceData?.amount || '0'}</Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Computer generated document.
                  </Typography>
                  <Chip 
                    label={invoiceData?.payment_status?.toUpperCase() || 'PAID'} 
                    color="success" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }} 
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={!invoiceData}
          sx={{ borderRadius: 2, fontWeight: 'bold' }}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;